/**
 * Dominio del embudo de ideas. Puro y determinista: el tiempo entra por `now`.
 * Contrato: docs/contracts/embudo-v1.md — el puente Python implementa lo mismo.
 */

export type Autor = 'carlos' | 'jarvis' | 'mercurius' | 'grok';
export type EstadoIdea = 'capturada' | 'madurando' | 'descartada' | 'promovida';
export type TipoDestino = 'proyecto' | 'mesa-editorial' | 'otro';
export type Accion =
  | 'capturar' | 'editar' | 'estado' | 'proponer' | 'rechazar' | 'promover' | 'restaurar' | 'borrar';

export interface Destino { tipo: TipoDestino; ref: string }
export interface Propuesta extends Destino { motivo: string; autor: Autor; fecha: number }
export interface Origen { autor: Autor; canal: string; ref: string; capturadaEn: number }
export interface Historia {
  fecha: number; autor: Autor; accion: Accion; motivo: string; campo: string; anterior: unknown;
}

export interface Idea {
  id: string;
  kind: 'idea';
  texto: string;
  notas: string;
  estado: EstadoIdea;
  destino: Destino | null;
  propuesta: Propuesta | null;
  origen: Origen;
  tags: string[];
  relacionadas: string[];
  historial: Historia[];
  pendienteRevision: boolean;
  createdAt: number;
  updatedAt: number;
  deleted: boolean;
}

export type CampoEditable = 'texto' | 'notas' | 'tags' | 'relacionadas';
export type PatchIdea = Partial<Pick<Idea, CampoEditable>>;

export const ESTADOS: EstadoIdea[] = ['capturada', 'madurando', 'promovida', 'descartada'];
export const TIPOS_DESTINO: TipoDestino[] = ['proyecto', 'mesa-editorial', 'otro'];
export const TRANSICIONES: Record<EstadoIdea, EstadoIdea[]> = {
  capturada: ['madurando', 'descartada'],
  madurando: ['capturada', 'descartada'],
  descartada: ['capturada'],
  promovida: []
};
const EDITABLES: CampoEditable[] = ['texto', 'notas', 'tags', 'relacionadas'];
const RESTAURABLES = ['texto', 'notas', 'tags', 'relacionadas', 'estado'];
const MAX_HISTORIAL = 200;
export const MAX_MOTIVO = 500;
/** campo → [máx. ítems, máx. caracteres por ítem] */
const MAX_LISTAS: Record<string, [number, number]> = { tags: [30, 60], relacionadas: [100, 64] };
/**
 * JSON UTF-8 del registro completo. El Worker rechaza payloads > 256 KiB (base64 ≈ 4/3 del texto
 * plano + 31 bytes de v2/iv/tag): 190 000 deja margen; 200 000 ya cifraría a ~266 700 > 262 144.
 */
export const MAX_REGISTRO_BYTES = 190_000;

export class IdeaError extends Error {
  constructor(public code: 'forbidden' | 'conflict' | 'invalid', msg: string) {
    super(msg);
    this.name = 'IdeaError';
  }
}

export function esAgente(a: Autor): boolean {
  return a !== 'carlos';
}

export function isIdea(r: unknown): r is Idea {
  return !!r && typeof r === 'object' && (r as { kind?: unknown }).kind === 'idea';
}

function soloCarlos(autor: Autor, que: string): void {
  if (esAgente(autor)) throw new IdeaError('forbidden', `Solo Carlos puede ${que}`);
}

function checkVersion(idea: Idea, version?: number): void {
  if (version !== undefined && version !== idea.updatedAt) {
    throw new IdeaError('conflict', 'La idea cambió desde que la leíste');
  }
}

function checkMotivo(autor: Autor, motivo: unknown): void {
  if (typeof motivo !== 'string' || motivo.length > MAX_MOTIVO) {
    throw new IdeaError('invalid', `El motivo debe ser string de hasta ${MAX_MOTIVO} caracteres`);
  }
  if (esAgente(autor) && !motivo.trim()) throw new IdeaError('invalid', 'Falta el motivo');
}

export function tamanoRegistro(rec: unknown): number {
  return new TextEncoder().encode(JSON.stringify(rec)).length;
}

function checkTamano<T>(rec: T): T {
  if (tamanoRegistro(rec) > MAX_REGISTRO_BYTES) throw new IdeaError('invalid', 'La idea es demasiado grande');
  return rec;
}

function checkVivo(idea: Idea): void {
  if (idea.deleted) throw new IdeaError('invalid', 'La idea está borrada');
}

function checkTexto(t: unknown): string {
  if (typeof t !== 'string') throw new IdeaError('invalid', 'El texto debe ser string');
  const v = t.trim();
  if (v.length < 1 || v.length > 2000) throw new IdeaError('invalid', 'El texto debe tener 1–2000 caracteres');
  return v;
}

function checkNotas(n: unknown): string {
  if (typeof n !== 'string' || n.length > 20000) throw new IdeaError('invalid', 'Las notas deben tener hasta 20000 caracteres');
  return n;
}

function checkLista(v: unknown, campo: string): string[] {
  const [maxItems, maxLen] = MAX_LISTAS[campo];
  if (!Array.isArray(v) || !v.every((x) => typeof x === 'string')) {
    throw new IdeaError('invalid', `${campo} debe ser lista de strings`);
  }
  if (v.length > maxItems || v.some((x: string) => x.length > maxLen)) {
    throw new IdeaError('invalid', `${campo}: hasta ${maxItems} ítems de hasta ${maxLen} caracteres`);
  }
  return [...v];
}

/** Valida un valor de un campo restaurable/editable según su tipo. */
function checkCampo(campo: string, v: unknown): unknown {
  if (campo === 'texto') return checkTexto(v);
  if (campo === 'notas') return checkNotas(v);
  if (campo === 'tags' || campo === 'relacionadas') return checkLista(v, campo);
  if (campo === 'estado') {
    if (!ESTADOS.includes(v as EstadoIdea)) throw new IdeaError('invalid', 'Estado desconocido');
    return v;
  }
  throw new IdeaError('invalid', `Campo ${campo} no editable`);
}

function checkDestino(d: Destino): Destino {
  if (!TIPOS_DESTINO.includes(d.tipo)) throw new IdeaError('invalid', 'Tipo de destino inválido');
  return { tipo: d.tipo, ref: (d.ref ?? '').trim() };
}

function mutar(idea: Idea, autor: Autor, now: number, cambios: Partial<Idea>, entradas: Historia[]): Idea {
  const historial = [...idea.historial, ...entradas].slice(-MAX_HISTORIAL);
  return checkTamano({ ...idea, ...cambios, historial, updatedAt: now, pendienteRevision: esAgente(autor) });
}

function igual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export interface CapturaInput {
  texto: string;
  notas?: string;
  tags?: string[];
  canal: string;
  ref?: string;
}

export function capturar(input: CapturaInput, autor: Autor, now: number, id: string): Idea {
  const texto = checkTexto(input.texto);
  return checkTamano<Idea>({
    id,
    kind: 'idea',
    texto,
    notas: checkNotas(input.notas ?? ''),
    estado: 'capturada',
    destino: null,
    propuesta: null,
    origen: { autor, canal: input.canal, ref: input.ref ?? '', capturadaEn: now },
    tags: checkLista(input.tags ?? [], 'tags'),
    relacionadas: [],
    historial: [{ fecha: now, autor, accion: 'capturar', motivo: '', campo: '', anterior: null }],
    pendienteRevision: esAgente(autor),
    createdAt: now,
    updatedAt: now,
    deleted: false
  });
}

export function editar(
  idea: Idea, autor: Autor, patch: PatchIdea, motivo: string, now: number, version?: number
): Idea {
  checkVivo(idea);
  if (esAgente(autor) && idea.estado === 'promovida') {
    throw new IdeaError('forbidden', 'Una idea promovida no la edita un agente');
  }
  checkVersion(idea, version);
  checkMotivo(autor, motivo);
  const cambios: Partial<Idea> = {};
  const entradas: Historia[] = [];
  for (const campo of EDITABLES) {
    const nuevo = patch[campo];
    if (nuevo === undefined) continue;
    const valor = checkCampo(campo, nuevo);
    if (igual(valor, idea[campo])) continue;
    (cambios as Record<string, unknown>)[campo] = valor;
    entradas.push({ fecha: now, autor, accion: 'editar', motivo, campo, anterior: idea[campo] });
  }
  if (entradas.length === 0) return idea;
  return mutar(idea, autor, now, cambios, entradas);
}

export function cambiarEstado(
  idea: Idea, autor: Autor, nuevo: EstadoIdea, motivo: string, now: number, version?: number
): Idea {
  checkVivo(idea);
  checkVersion(idea, version);
  checkMotivo(autor, motivo);
  if (!TRANSICIONES[idea.estado].includes(nuevo)) {
    throw new IdeaError('invalid', `No se puede pasar de ${idea.estado} a ${nuevo}`);
  }
  // Descartar limpia la propuesta pendiente; la entrada de estado alcanza como rastro.
  const cambios: Partial<Idea> = nuevo === 'descartada' ? { estado: nuevo, propuesta: null } : { estado: nuevo };
  return mutar(idea, autor, now, cambios, [
    { fecha: now, autor, accion: 'estado', motivo, campo: 'estado', anterior: idea.estado }
  ]);
}

export function proponerPromocion(
  idea: Idea, autor: Autor, destino: Destino, motivo: string, now: number, version?: number
): Idea {
  checkVivo(idea);
  checkVersion(idea, version);
  checkMotivo(autor, motivo);
  if (idea.estado !== 'capturada' && idea.estado !== 'madurando') {
    throw new IdeaError('invalid', 'Solo se propone desde capturada o madurando');
  }
  const d = checkDestino(destino);
  return mutar(idea, autor, now, { propuesta: { ...d, motivo, autor, fecha: now } }, [
    { fecha: now, autor, accion: 'proponer', motivo, campo: 'propuesta', anterior: idea.propuesta }
  ]);
}

export function promover(idea: Idea, autor: Autor, destino: Destino, now: number): Idea {
  soloCarlos(autor, 'promover');
  checkVivo(idea);
  if (idea.estado !== 'capturada' && idea.estado !== 'madurando') {
    throw new IdeaError('invalid', 'Solo se promueve desde capturada o madurando');
  }
  const d = checkDestino(destino);
  return mutar(idea, autor, now, { estado: 'promovida', destino: d, propuesta: null }, [
    { fecha: now, autor, accion: 'promover', motivo: '', campo: 'estado', anterior: idea.estado }
  ]);
}

export function rechazarPropuesta(idea: Idea, autor: Autor, now: number): Idea {
  soloCarlos(autor, 'rechazar propuestas');
  checkVivo(idea);
  if (!idea.propuesta) return idea;
  return mutar(idea, autor, now, { propuesta: null }, [
    { fecha: now, autor, accion: 'rechazar', motivo: '', campo: 'propuesta', anterior: idea.propuesta }
  ]);
}

export function restaurar(idea: Idea, autor: Autor, index: number, now: number): Idea {
  soloCarlos(autor, 'restaurar versiones');
  checkVivo(idea);
  const e = idea.historial[index];
  if (!e || !RESTAURABLES.includes(e.campo)) throw new IdeaError('invalid', 'Esa entrada no se puede restaurar');
  if (e.campo === 'estado' && idea.estado === 'promovida') {
    throw new IdeaError('invalid', 'Una idea promovida no cambia de estado');
  }
  if (e.campo === 'estado' && e.anterior === 'promovida') {
    throw new IdeaError('invalid', 'No se restaura a promovida');
  }
  const valor = checkCampo(e.campo, e.anterior);
  const campo = e.campo as keyof Idea;
  const cambios = { [campo]: valor } as Partial<Idea>;
  if (campo === 'estado' && valor === 'descartada') cambios.propuesta = null;
  return mutar(idea, autor, now, cambios, [
    { fecha: now, autor, accion: 'restaurar', motivo: '', campo: e.campo, anterior: idea[campo] }
  ]);
}

export function marcarRevisado(idea: Idea, autor: Autor, now: number): Idea {
  soloCarlos(autor, 'marcar como revisado');
  if (!idea.pendienteRevision) return idea;
  return { ...idea, pendienteRevision: false, updatedAt: now };
}

export function borrar(idea: Idea, autor: Autor, now: number): Idea {
  soloCarlos(autor, 'borrar');
  return mutar(idea, autor, now, { deleted: true }, [
    { fecha: now, autor, accion: 'borrar', motivo: '', campo: 'deleted', anterior: false }
  ]);
}
